declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
  // Runtime implementation defined in index.html bootstrap script
  function gtag(...args: unknown[]): void;
}

// Automated browsers (Playwright sets `navigator.webdriver`) stamp every event
// `e2e_test: true`, so a test run that reaches production GA4 — the opt-in
// `test:e2e:site` canary blocks GA hosts, but any leak — stays filterable in
// reports (custom dimension `e2e_test`). (#571)
/** Where the visitor's app came from — `wizard` (`/create`) or `freeform`
 * (`/demo`). Lives in sessionStorage so it follows them across a full-page
 * hop into the site (custom dimension `source`). (#1445) */
export type AnalyticsSource = "wizard" | "freeform";

export const SOURCE_KEY = "demo.source";

export const setAnalyticsSource = (source: AnalyticsSource): void => {
  try {
    sessionStorage.setItem(SOURCE_KEY, source);
  } catch {
    // Storage blocked (private mode, sandboxed iframe) — events go unmarked.
  }
};

// Read at call time, not at init: the marker is set after the singleton is
// built, and must reach a later `demo.signup.from-demo` in the same tab.
const sourceMarker = (): Record<string, unknown> => {
  try {
    const source = sessionStorage.getItem(SOURCE_KEY);
    return source ? { source } : {};
  } catch {
    return {};
  }
};

export const createAnalytics = (
  measurementId: string | undefined,
  pathname: string,
  automated: boolean = false,
) => {
  let initialized = false;
  const marker: Record<string, unknown> = automated ? { e2e_test: true } : {};

  const initAnalytics = (): void => {
    if (initialized || !measurementId || pathname.startsWith("/admin")) return;

    // Dynamically insert the gtag.js script
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    gtag("js", new Date());
    gtag("config", measurementId, { send_page_view: false });
    initialized = true;
  };

  const trackPageview = (
    orgId: string,
    username: string,
    embedMode: boolean = false,
  ): void => {
    if (!initialized) return;
    gtag("event", "page_view", {
      org_id: orgId,
      org_username: username,
      embed_mode: embedMode,
      ...sourceMarker(),
      ...marker,
    });
  };

  const trackEvent = (
    name: string,
    params: Record<string, unknown> = {},
  ): void => {
    if (!initialized) return;
    gtag("event", name, { ...sourceMarker(), ...params, ...marker });
  };

  return { initAnalytics, trackPageview, trackEvent };
};

// 32-bit FNV-1a hash, hex-encoded. Stable across runs/reloads.
// Used to attribute funnel events to a prompt without leaking the text.
export const hashPrompt = (text: string): string => {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
};

// Singleton for app usage. __GA4_MEASUREMENT_ID__ is replaced at build time
// by Vite's define config (same pattern as __BUILD_COMMIT__).
const _measurementId: string | undefined =
  __GA4_MEASUREMENT_ID__ || undefined;
const _pathname =
  typeof window !== "undefined" ? window.location.pathname : "/";
const _automated =
  typeof navigator !== "undefined" && navigator.webdriver === true;

const { initAnalytics, trackPageview, trackEvent } = createAnalytics(
  _measurementId,
  _pathname,
  _automated,
);

export { initAnalytics, trackPageview, trackEvent };
